import { AxiosRequestConfig } from 'axios';
import { responseContract } from './api.lib';
import { apiClient, unwrap } from './api-client';
import { oneIdentifier } from '~shared/config/hostname';
import {
  AddToCartDto,
  BookDto,
  UpdateCartItemDto,
  UpdateInsuranceOfferDto,
  UpdatePromotionDto,
  UpdateUserDto,
  ValidateCheckoutDto,
} from './api.types';
import { api } from './api.instance';
import {
  AddToCartDtoSchema,
  BookDtoSchema,
  InquiryDtoSchema,
  OffersDtoSchema,
  PaymentDtoSchema,
  SessionDtoSchema,
  UpdateCartItemDtoSchema,
  UpdatePromotionDtoSchema,
  UpdateUserDtoSchema,
  ValidateCheckoutDtoSchema,
  ValidateCheckoutResponseDtoSchema,
  UpdateInsuranceOfferDtoSchema,
} from './api.contracts';
import { OffersQueryDto } from '~entities/offer/model/offer.types';
import { Locale } from '~app/i18n';
import {
  DayQueryDto,
  MonthQueryDto,
} from '~features/booking/booking-calendar/model/calendar.types';
import {
  CalendarDayQueryDtoSchema,
  CalendarDaySchema,
  CalendarMonthQueryDtoSchema,
  CalendarMonthSchema,
} from '~features/booking/booking-calendar/model/calendar.contracts';
import TagManager from 'react-gtm-module';
import { getOfferingById } from '~entities/offering/lib/offering.utils';
import { CartItem } from '~entities/session/model/session.types';
import { BookingDetailSchema } from '~entities/booking/model/booking.contracts';

export const getConfiguration = (
  locale: Locale = 'en',
  init?: { signal?: AbortSignal },
) =>
  unwrap(
    apiClient.GET('/rest/v1/samera-one/{oneIdentifier}/configuration', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      signal: init?.signal,
    }),
  );

export const addVariant = (
  variantId: string,
  config?: AxiosRequestConfig<{ variantId: string }>,
) => {
  return api
    .post(
      '/session/items/variants',
      {
        variantId,
      },
      config,
    )
    .then(responseContract(SessionDtoSchema));
};

export const updateUser = (
  updateUserDto: UpdateUserDto,
  locale: Locale = 'en',
  config?: AxiosRequestConfig<UpdateUserDto>,
) => {
  const data = UpdateUserDtoSchema.parse(updateUserDto);
  return api
    .put(`/session/user?lang=${locale}`, data, config)
    .then(responseContract(SessionDtoSchema));
};

export const getOffers = (
  payload: OffersQueryDto,
  locale: Locale = 'en',
  config?: AxiosRequestConfig,
) => {
  return api
    .post(`/offers?lang=${locale}`, payload, config)
    .then(responseContract(OffersDtoSchema));
};

export const getSession = (
  locale: Locale = 'en',
  config?: AxiosRequestConfig,
) => {
  return api
    .get(`/session?lang=${locale}`, config)
    .then(responseContract(SessionDtoSchema));
};

export const getLastBooking = (
  locale: Locale = 'en',
  init?: { signal?: AbortSignal },
) =>
  unwrap(
    apiClient.GET('/rest/v1/samera-one/{oneIdentifier}/session/last-booking', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      signal: init?.signal,
    }),
  );

export const getBookingDetail = (
  bookingId: string,
  email: string,
  locale: Locale = 'en',
  config?: AxiosRequestConfig,
) => {
  return api
    .get(`/bookings/${encodeURIComponent(bookingId)}`, {
      ...config,
      params: { ...config?.params, email, lang: locale },
    })
    .then(responseContract(BookingDetailSchema));
};

export const addToCart = (
  addToCartDto: AddToCartDto,
  config?: AxiosRequestConfig<AddToCartDto>,
) => {
  const data = AddToCartDtoSchema.parse(addToCartDto);

  const room = getOfferingById(data.roomCategoryId);

  TagManager.dataLayer({
    dataLayer: {
      event: 'add_to_cart',
      ecommerce: {
        currency: data.price.currency,
        value: data.price.amount,
        items: [
          {
            item_name: room ? room.name : 'Unknown',
            item_id: data.roomCategoryId,
            price: data.price.amount,
            quantity: 1,
          },
        ],
      },
    },
  });

  return api
    .post('/session/items', data, config)
    .then(responseContract(SessionDtoSchema));
};

export const updateCartItem = (
  updateCartItemDto: UpdateCartItemDto,
  config?: AxiosRequestConfig<UpdateCartItemDto>,
) => {
  const data = UpdateCartItemDtoSchema.parse(updateCartItemDto);
  return api
    .put(`/session/items/${data.id}`, data, config)
    .then(responseContract(SessionDtoSchema));
};

export const removeFromCart = (
  cartItem: CartItem,
  config?: AxiosRequestConfig<CartItem>,
) => {
  const room = getOfferingById(cartItem.roomId);

  TagManager.dataLayer({
    dataLayer: {
      event: 'remove_from_cart',
      ecommerce: {
        currency: cartItem.price.currency,
        value: cartItem.price.amount,
        items: [
          {
            item_name: room ? room.name : 'Unknown',
            item_id: cartItem.roomId,
          },
        ],
      },
    },
  });

  return api
    .delete(`/session/items/${cartItem.id}`, config)
    .then(responseContract(SessionDtoSchema));
};

export const validateCheckout = (
  validateCheckoutDto: ValidateCheckoutDto,
  locale: Locale = 'en',
  config?: AxiosRequestConfig,
) => {
  const data = ValidateCheckoutDtoSchema.parse(validateCheckoutDto);
  return api
    .post(`/checkout/validate?lang=${locale}`, data, config)
    .then(responseContract(ValidateCheckoutResponseDtoSchema));
};

export const book = (
  bookDto: BookDto,
  locale: Locale = 'en',
  config?: AxiosRequestConfig,
) => {
  const data = BookDtoSchema.parse(bookDto);
  return api
    .post(`/checkout/book?lang=${locale}`, data, config)
    .then(responseContract(PaymentDtoSchema));
};

export const inquire = (
  bookDto: BookDto,
  locale: Locale = 'en',
  config?: AxiosRequestConfig,
) => {
  const data = BookDtoSchema.parse(bookDto);
  return api
    .post(`/checkout/inquire?lang=${locale}`, data, config)
    .then(responseContract(InquiryDtoSchema));
};

export const getMonth = (
  payload: MonthQueryDto,
  config?: AxiosRequestConfig,
) => {
  const data = CalendarMonthQueryDtoSchema.parse(payload);
  return api
    .post(`/calendar/month`, data, config)
    .then(responseContract(CalendarMonthSchema));
};

export const getDay = (payload: DayQueryDto, config?: AxiosRequestConfig) => {
  const data = CalendarDayQueryDtoSchema.parse(payload);
  return api
    .post(`/calendar/day`, data, config)
    .then(responseContract(CalendarDaySchema));
};

export const updatePromotion = (
  payload: UpdatePromotionDto,
  config?: AxiosRequestConfig,
) => {
  const data = UpdatePromotionDtoSchema.parse(payload);
  return api
    .put(`/session/promotion`, data, config)
    .then(responseContract(SessionDtoSchema));
};

export const updateInsuranceOffer = (
  payload: UpdateInsuranceOfferDto,
  config?: AxiosRequestConfig,
) => {
  const data = UpdateInsuranceOfferDtoSchema.parse(payload);
  return api
    .put(`/session/erv-insurance-offer`, data, config)
    .then(responseContract(SessionDtoSchema));
};
